import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth.jsx';
import { useTheme } from '../../hooks/useTheme.jsx';
import { apiClient } from '../../services/http.js';
import { CREDITS_UPDATED_EVENT } from '../../services/creditsEvents.js';

const PLAN_LABELS = { basic: 'Plano Básico', regular: 'Plano Regular', premium: 'Plano Premium' };

/**
 * Bloco compacto de conta no rodapé da sidebar/menu "Mais" (P23E): plano e
 * créditos numa linha, avatar/nome, e um menu com Perfil/Plano, Configurações,
 * Tema e Sair — consolida controles que antes só existiam dentro do Chat
 * (tema, configurações, logout), tornando-os acessíveis em qualquer página.
 */
export const AccountMenu = ({ collapsed = false, onOpenSettings }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [credits, setCredits] = useState(null);
  const [plan, setPlan] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    apiClient
      .request('/billing/credits')
      .then(({ res, data }) => {
        if (res.ok) {
          setCredits(data?.available ?? 0);
          setPlan(data?.plan && data.plan !== 'trial' ? data.plan : null);
        }
      })
      .catch(() => null); // bloco de conta não deve travar a página se o billing falhar
  }, []);

  // O chat avisa quando o saldo muda (fim de cada resposta); sem isto o número ficava parado
  // no valor lido ao montar a página.
  useEffect(() => {
    const onCreditsUpdated = (e) => {
      const detail = e.detail || {};
      if (typeof detail.available === 'number') setCredits(detail.available);
      if ('plan' in detail) setPlan(detail.plan && detail.plan !== 'trial' ? detail.plan : null);
    };
    window.addEventListener(CREDITS_UPDATED_EVENT, onCreditsUpdated);
    return () => window.removeEventListener(CREDITS_UPDATED_EVENT, onCreditsUpdated);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    const handleKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, []);

  const initial = (user?.name || user?.email || '?').trim().charAt(0).toUpperCase();

  return (
    <div ref={containerRef} className="relative">
      {open && (
        <div className={`absolute bottom-full mb-2 ${collapsed ? 'left-0' : 'left-0 right-0'} w-56 max-w-[calc(100vw-1.5rem)] bg-surface-container-low border border-outline-variant/20 rounded-xl shadow-xl py-1.5 z-50`}>
          <button
            onClick={() => { setOpen(false); navigate('/billing'); }}
            className="w-full flex items-center gap-3 md:gap-2.5 px-3 py-2 min-h-control md:py-1.5 md:min-h-control-sm text-sm md:text-[13px] text-on-surface/80 hover:text-on-surface hover:bg-primary/5 transition-colors"
          >
            <span className="material-symbols-outlined text-[18px] md:text-[16px]">workspace_premium</span>
            Perfil/Plano
          </button>
          <button
            onClick={() => { setOpen(false); onOpenSettings?.(); }}
            className="w-full flex items-center gap-3 md:gap-2.5 px-3 py-2 min-h-control md:py-1.5 md:min-h-control-sm text-sm md:text-[13px] text-on-surface/80 hover:text-on-surface hover:bg-primary/5 transition-colors"
          >
            <span className="material-symbols-outlined text-[18px] md:text-[16px]">settings</span>
            Configurações
          </button>
          <button
            onClick={toggleTheme}
            className="w-full flex items-center gap-3 md:gap-2.5 px-3 py-2 min-h-control md:py-1.5 md:min-h-control-sm text-sm md:text-[13px] text-on-surface/80 hover:text-on-surface hover:bg-primary/5 transition-colors"
          >
            <span className="material-symbols-outlined text-[18px] md:text-[16px]">{theme === 'dark' ? 'light_mode' : 'dark_mode'}</span>
            {theme === 'dark' ? 'Modo claro' : 'Modo escuro'}
          </button>
          {user?.role === 'admin' && (
            <button
              onClick={() => { setOpen(false); navigate('/admin'); }}
              className="w-full flex items-center gap-3 md:gap-2.5 px-3 py-2 min-h-control md:py-1.5 md:min-h-control-sm text-sm md:text-[13px] text-on-surface/80 hover:text-on-surface hover:bg-primary/5 transition-colors"
            >
              <span className="material-symbols-outlined text-[18px] md:text-[16px]">admin_panel_settings</span>
              Painel Admin
            </button>
          )}
          <div className="my-1 border-t border-outline-variant/10" />
          <button
            onClick={async () => { setOpen(false); await logout(); navigate('/'); }}
            className="w-full flex items-center gap-3 md:gap-2.5 px-3 py-2 min-h-control md:py-1.5 md:min-h-control-sm text-sm md:text-[13px] text-on-surface/80 hover:text-error hover:bg-error/5 transition-colors"
          >
            <span className="material-symbols-outlined text-[18px] md:text-[16px]">logout</span>
            Sair
          </button>
        </div>
      )}

      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Conta"
        aria-expanded={open}
        className={`w-full flex items-center gap-2.5 md:gap-2 rounded-lg hover:bg-primary/5 transition-colors min-h-control md:min-h-control-sm ${collapsed ? 'justify-center p-2 md:p-1.5' : 'px-2 py-2 md:py-1.5'}`}
      >
        <div className="w-8 h-8 md:w-6 md:h-6 rounded-full bg-primary/15 text-primary flex items-center justify-center font-semibold text-sm md:text-xs shrink-0">
          {initial}
        </div>
        {!collapsed && (
          <div className="min-w-0 text-left">
            <div className="text-sm md:text-[13px] text-on-surface truncate">{user?.name || user?.email || 'Minha conta'}</div>
            <div className="text-xs md:text-[11px] text-on-surface-variant/70 truncate">
              {credits !== null ? `${credits} créditos` : '—'}
              {plan ? ` · ${PLAN_LABELS[plan] ?? plan}` : ''}
            </div>
          </div>
        )}
      </button>
    </div>
  );
};
